import { useEventTriggerWidgets } from '../../../../features/widgets/hooks/useEventTriggerWidgets';
import type { EventTriggerWidgetType, WidgetEntry } from '../../../../features/widgets/types';
import { useStartGame } from '../../../../hooks/useStartGame';
import { useFlag } from '../../../../services/launchDarkly';
import { EnumOpenDialog } from '../../../../stores/ui/types';
import { useStores } from '../../../../stores/useStores';

import { resolveBannerCtaAction } from './bannerCtaAction';

export const useBannerCta = () => {
  const {
    uiStore,
    rewardsStore,
    hammersStore,
    trailStore,
    puzzleStore,
    sportEventsStore,
    jackpotStore,
    gameUiStore,
    eventTriggerStore,
    tournamentStore,
    tournamentOfChampionsStore,
  } = useStores();
  const { handleStartGame } = useStartGame();
  const inviteFriendsEnabled = useFlag<boolean>('invite_friends_enabled', true);
  const isCrownSportsEnabled = useFlag('crown_sports_feature', false);
  const monetizationEventsEnabled = useFlag('monetization_events_enabled', true);
  const metagamesEnabled = useFlag('metagames_enabled', true);
  // useEventTriggerWidgets only ever emits these 4 widget types; the cast documents that instead
  // of re-deriving its offers_feat/messages_feat/road_feature_web gating here.
  const eventTriggerWidgets = useEventTriggerWidgets() as WidgetEntry<EventTriggerWidgetType>[];

  return resolveBannerCtaAction(uiStore.bannerCta, {
    startGame: (gameId) => handleStartGame(gameId, 'lobby_banner'),
    inviteFriendsEnabled,
    openInviteFriends: () => {
      void rewardsStore.fetchReferralData();
      uiStore.setOpenDialog({
        dialog: EnumOpenDialog.isOpenInviteFriendsNew,
      });
    },
    tournamentOfChampionsAvailable: tournamentOfChampionsStore.tournamentOfChampions != null,
    getTournamentCanOpenDialog: () => tournamentOfChampionsStore.canOpenDialog,
    fetchTournament: async () => {
      await tournamentStore.fetch();
    },
    openTournamentDialog: () =>
      uiStore.setOpenDialog({
        dialog: EnumOpenDialog.isOpenTournamentDialog,
        options: { shown_by: 'widget_click' },
      }),
    crownSportsFeatureEnabled: isCrownSportsEnabled,
    sportEventAvailable: monetizationEventsEnabled && sportEventsStore.sportEvent != null,
    startSportEvent: () => void sportEventsStore.startSportEvent(),
    crownJackpotAvailable: jackpotStore.canShowWidget,
    openCrownJackpot: () => gameUiStore.openJackpotDialog({ source: 'cta' }),
    benefitEventAvailable: (benefitKey) =>
      eventTriggerWidgets.some((entry) => entry.data.event.benefit.key === benefitKey),
    openBenefitEvent: (benefitKey) => {
      const found = eventTriggerWidgets.find((entry) => entry.data.event.benefit.key === benefitKey);
      if (found) void eventTriggerStore.openPopupByEventTriggerId(found.data.event.eventTrigger.eventTriggerId);
    },
    metagameAvailable: {
      hammers: metagamesEnabled && hammersStore.hammersEventByCoin != null,
      trail: metagamesEnabled && trailStore.trailEventByCoin != null,
      puzzle: metagamesEnabled && puzzleStore.puzzleEventByCoin != null,
    },
    startMetagame: {
      hammers: () => void hammersStore.startHammersGame(),
      trail: () => void trailStore.startTrailGame(),
      puzzle: () => void puzzleStore.startPuzzleGame(),
    },
  });
};
